import { useMemo } from 'react';
import { Attendee } from './typings';
import { useSingleSchema } from '../../../toolympus/hooks/useSchema';
import { useSmartList } from '../../../toolympus/hooks/useSmartList';
import { schemaWithConfigurableFields, useAttendeeProfileConfiguration } from './useAttendeeProfileConfiguration';
import { exportRecordsToXlsxSheet } from '../../../toolympus/api/xlsx';
import { apiFetch } from '../../../toolympus/api/core';
import { useAction } from '../../../toolympus/api/useAction';

export const ApiPath = "/api/attendee";
export const AttendeesApiPath = `${ApiPath}/manage`


export const attendeeSearchText = (r: Attendee) => `${r.display_name} ${r.email || ""}`

export const useAttendeesList = () => {
  const { schema: schemaBase } = useSingleSchema(`${AttendeesApiPath}/uiconfig`)
  
  // const approvalStatusFilter = useBrowserStoredValue("all", "status", "__pl_approval_status");
  // const extraFilter = useBrowserStoredValue("all", "extra", "__pl_extra");

  const data = useSmartList<Attendee>(AttendeesApiPath, {
    newRecordDefault: {},
    schema: schemaBase,
    getRecordApiPath: item => `${AttendeesApiPath}/${item._id}`,
    extraParams: {
      // approval_status: approvalStatusFilter.value === "all" ? "" : approvalStatusFilter.value,
    },
    textFilterFn: attendeeSearchText,
    sorting: {
      allowedFields: [
        "display_name",
        "email",
        "created_at",
      ]
    },
    editRecordSettings: {
      dontResetOnSave: true,
      save: async (item, changes) => {
        if(item.info) {
          const result = await apiFetch<Attendee>(`${AttendeesApiPath}/${item._id}`, "put", { info: item.info })
          return result;
        }
        return item;
      }
    },
  });

  const config = useAttendeeProfileConfiguration();

  const schema = useMemo(
    () => schemaWithConfigurableFields(
      schemaBase,
      config.data.fields,
      { cropTitles: true }),
    [schemaBase, config.data]);

  const dataX = useMemo(() => {
    const d = data.data.map(s => {
      const copy = { ...s } as any;
      config.data.fields.forEach(f => {
        copy[f._id] = s.info[f._id];
      });
      return copy;
    });

    return d;
  }, [data.data, config.data]);

  const exportAllRecords = async () => {
    const records = await apiFetch<Attendee[]>(AttendeesApiPath)
    exportRecordsToXlsxSheet<Attendee>({
      sheetTitle: "Attendees all",
      schema,
      filename: `riac_attendees.xlsx`,
      columns: [
        "email",
        ...config.data.fields.map(f => f._id),
        "created_at",
      ],
      prepare: r => ({
        ...r,
        ...(r.info || {}),
      })
    }, records);
  }

  const exportAllWithRegRecords = async () => {
    const records = await apiFetch<Attendee[]>(`${AttendeesApiPath}/with-registrations`);
    exportRecordsToXlsxSheet<Attendee>({
      sheetTitle: "Attendees with registration",
      schema: {
      ...schema,
      events: { label: "Events" },
      events_count: { label: "Events #" },
    },
      filename: `riac_attendees.xlsx`,
      columns: [
        "email",
        ...config.data.fields.map(f => f._id),
        "events",
        "events_count",
        "created_at",
      ],
      prepare: r => ({
        ...r,
        ...(r.info || {}),
      })
    }, records);
  }

  const exportAllRecordsAction = useAction(exportAllRecords);
  const exportAllWithRegRecordsAction = useAction(exportAllWithRegRecords);

  return {
    ...data,
    data: dataX,
    config,
    schema,
    review: data.editRecord,
    remove: data.remove,

    exportAllRecordsAction,
    exportAllWithRegRecordsAction,
  }
}


